import { CloseOutlined, DeleteOutline, MapOutlined } from '@mui/icons-material';
import {
    AppBar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Toolbar,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { attConverter } from '../common/util/utils';
import EngineIcon from '../resources/images/data/engine.svg?react';
import MapComponent from './components/MapComponent';
import './MonitoreoPage.css';

// --- Async WebGL Context Detector (unchanged) ---
async function detectMaxWebGLContextsAsync(maxTest = 128, delayMs = 20) {
    const created = [];
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const releaseAll = () => {
        for (const { gl } of created) {
            if (!gl) continue;
            const loseExt = gl.getExtension('WEBGL_lose_context');
            if (loseExt) {
                try {
                    loseExt.loseContext();
                } catch (_) { }
            }
        }
    };
    try {
        for (let i = 0; i < maxTest; i++) {
            const canvas = document.createElement('canvas');
            const gl =
                canvas.getContext('webgl2') ||
                canvas.getContext('webgl') ||
                canvas.getContext('experimental-webgl');
            if (!gl) {
                releaseAll();
                return created.length;
            }
            created.push({ canvas, gl });
            await sleep(delayMs);
            const anyLost = created.some((c) => {
                try {
                    return typeof c.gl.isContextLost === 'function' && c.gl.isContextLost();
                } catch {
                    return true;
                }
            });
            if (anyLost) {
                const maxAllowed = Math.max(0, created.length - 1);
                releaseAll();
                return maxAllowed;
            }
        }
    } catch (err) {
        console.warn('Error during WebGL context detection:', err);
    }
    releaseAll();
    return created.length;
}

const MonitoreoPage = () => {
    const navigate = useNavigate();
    const devices = Object.values(useSelector((state) => state.devices.items));
    const positions = useSelector((state) => state.session.positions);
    const events = useSelector((state) => state.events.items);

    const [selectedDevices, setSelectedDevices] = useState([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [maxDevices, setMaxDevices] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeMapId, setActiveMapId] = useState(null);

    const theme = useTheme();

    const TYPOGRAPHY_FONT_SIZE = 16;

    useEffect(() => {
        const detect = async () => {
            setLoading(true);
            const detected = await detectMaxWebGLContextsAsync(64, 25);
            const safeLimit = Math.max(1, detected - 1);
            setMaxDevices(safeLimit);
            setLoading(false);
            window.alertify.alert('Info', `Detectadas ${safeLimit} unidades máximas.`, () => { });
        };
        detect();
    }, []);

    const toggleDevice = (deviceId) => {
        if (!positions[deviceId]) {
            window.alertify.alert('Error', 'Esta unidad no está reportando', () => { });
            return;
        }
        setSelectedDevices((prev) => {
            if (prev.includes(deviceId)) return prev.filter((id) => id !== deviceId);
            if (prev.length >= maxDevices) return [...prev.slice(1), deviceId];
            return [...prev, deviceId];
        });
    };

    const clearAllDevices = () => setSelectedDevices([]);
    const handleEngineOff = (deviceId) => window.confirmDialog(1, deviceId);
    const handleReactivateEngine = (deviceId) => window.confirmDialog(2, deviceId);

    // --- Loading State ---
    if (loading || maxDevices === null) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100vh" gap={2}>
                <CircularProgress size={48} />
                <Typography variant="h6">Calculando capacidad gráfica disponible...</Typography>
            </Box>
        );
    }

    return (
        <Box className="monitoreo-page" sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
            {/* Header */}
            <AppBar position="static" color="primary" elevation={2}>
                <Toolbar>
                    <Button color="inherit" onClick={() => (location.href = '/')} sx={{ mr: 2 }}>
                        ← Volver
                    </Button>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Monitoreo de Unidades
                    </Typography>
                    <Chip
                        color="secondary"
                        label={`Máximo: ${maxDevices}`}
                        variant="outlined"
                        sx={{ fontWeight: 'bold', color: 'white', borderColor: 'white' }}
                    />
                </Toolbar>
            </AppBar>

            {/* Controls */}
            <Box p={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Button variant="contained" startIcon={<MapOutlined />} onClick={() => setIsDrawerOpen(true)}>
                    Elegir unidades ({selectedDevices.length}/{maxDevices})
                </Button>
                {selectedDevices.length > 0 && (
                    <Button variant="outlined" color="error" startIcon={<DeleteOutline />} onClick={clearAllDevices}>
                        Desmarcar todo
                    </Button>
                )}
            </Box>

            {/* Drawer for device selection */}
            <Drawer anchor="right" open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
                <Box sx={{ width: 320, p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">Seleccione unidades</Typography>
                        <IconButton onClick={() => setIsDrawerOpen(false)}>
                            <CloseOutlined />
                        </IconButton>
                    </Box>
                    <Divider sx={{ my: 2 }} />

                    {devices.map((device) => {
                        const selected = selectedDevices.includes(device.id);
                        return (
                            <Box
                                key={device.id}
                                onClick={() => toggleDevice(device.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    bgcolor: selected ? theme.palette.action.selected : 'transparent',
                                    '&:hover': { bgcolor: theme.palette.action.hover },
                                }}
                            >
                                <Typography>{device.name}</Typography>
                                {selected && <Chip size="small" color="success" label="Activa" />}
                            </Box>
                        );
                    })}
                </Box>
            </Drawer>

            {/* Main Content */}
            <Box p={2}>
                {selectedDevices.length === 0 ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        height="70vh"
                        textAlign="center"
                    >
                        <Typography variant="h3" color="text.secondary">
                            🗺️
                        </Typography>
                        <Typography variant="h6">No hay unidades seleccionadas</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Presione "Elegir unidades" para comenzar
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {selectedDevices.map((id) => {
                            const device = devices.find((d) => d.id === id);
                            const position = positions[device.id];
                            const fixTime = new Date(position.fixTime).toLocaleString('es-ES', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            });

                            const isActive = activeMapId === id;

                            return (
                                <Grid item xs={12} sm={6} md={4} key={id}>
                                    <Card
                                        variant="outlined"
                                        sx={{ borderRadius: 3 }}
                                        onClick={() => setActiveMapId(id)}
                                        onMouseLeave={() => setActiveMapId(null)}
                                    >
                                        <CardHeader
                                            title={device.name}
                                            action={
                                                <IconButton onClick={() => toggleDevice(device.id)}>
                                                    <CloseOutlined />
                                                </IconButton>
                                            }
                                        />
                                        <CardContent>
                                            <MapComponent
                                                deviceName={device.name}
                                                position={position}
                                                size={352}
                                                apiKey="YOUR_GOOGLE_MAPS_KEY"
                                                interactive={isActive}
                                            />
                                            <Box mt={2}>
                                                <Typography variant="body2" sx={{ fontSize: TYPOGRAPHY_FONT_SIZE }}>
                                                    <strong>Lat:</strong> {position.latitude.toFixed(5)} &nbsp;|&nbsp;&nbsp;
                                                    <strong>Lng:</strong> {position.longitude.toFixed(5)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: TYPOGRAPHY_FONT_SIZE }}>
                                                    <strong>Posición:</strong> {fixTime}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: TYPOGRAPHY_FONT_SIZE }}>
                                                    <strong>Movimiento:</strong>{' '}
                                                    <span style={{ color: position.attributes.motion ? 'green' : 'red' }}>
                                                        {position.attributes.motion ? 'Sí' : 'No'}
                                                    </span>
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: TYPOGRAPHY_FONT_SIZE }}>
                                                    <strong>⏲:</strong>{' '}
                                                    <span style={{}}>
                                                        {attConverter(position, 'speed')}
                                                    </span>
                                                </Typography>
                                                <Tooltip title="Encendido del motor">
                                                    <EngineIcon
                                                        style={{
                                                            color: position.attributes.ignition ? '#036104' : 'red',
                                                            width: 28,
                                                            height: 28,
                                                            marginTop: 4,
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </CardContent>
                                        <Divider />
                                        <CardActions sx={{ justifyContent: 'flex-end' }}>
                                            <Button variant="contained" color="error" size="small" onClick={() => handleEngineOff(device.id)}>
                                                Apagar
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => handleReactivateEngine(device.id)}
                                            >
                                                Reactivar
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                )}
            </Box>
        </Box>
    );
};

export default MonitoreoPage;
